import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { activeFilterIsStudentFilter } from './StudentFilters';
import { Theme, styled, useMediaQuery, useTheme } from '@material-ui/core';
import { ImpersonateModal } from './ImpersonateModal';
import { useAppDispatch, useAppSelector } from '../hooks/useAppDispatch';
import { Dimensions } from '../slices/sidePanel/sidePanelSlice';
import { setModalProps } from '../slices/privatePageSettings/entity/privatePageSettingsSlice';
import {
  setActiveFilter,
  setSelectedGroup,
  setSelectedStudent,
} from '../slices/filters/entity/FiltersSlice';
import { useSnackbar } from 'notistack';
import NavigationBar from 'src/modules/shared/components/Navigationbar/Navigationbar';
import { useLocation } from 'react-router-dom';
import { AppStepper } from 'src/components/AppStepper/AppStepper';
//import { VideoPageHelper } from 'src/components/VideoPageHelper/VideoPageHelper';
import BlockAndSectionLoading from './Dialog/BlockAndSectionLoading/BlockAndSectionLoading';

const ExternalWrapper = styled('div')<Theme, { presentationmode?: boolean }>(
  ({ theme, presentationmode }) => ({
    display: 'flex',
    flexDirection: 'row',
    height: '100vh',
    overflow: 'hidden auto',
    'body:has(#banner) &': {
      paddingTop: theme.config.messageBarHeightPx,
      '&.has-subheader': {
        height: `calc(100vh - ${theme.config.subHeaderHeight + theme.config.messageBarHeight}px)`,
      },
    },
    '&.filters-active': {
      height: `calc(100vh - ${theme.config.appbarHeightPx})`,
      '&:not(.fullscreen)': {
        marginTop: presentationmode ? 0 : theme.config.appbarHeightPx,
      },
    },
    '&.has-subheader': {
      height: `calc(100vh - ${theme.config.subHeaderHeightPx})`,
      '&:not(.fullscreen)': {
        marginTop: presentationmode ? 0 : theme.config.subHeaderHeightPx,
      },
    },
  })
);

const ScrollContainer = styled('div')<
  Theme,
  {
    sidepanel?: Dimensions;
    toolboxvisible: string;
    navigationbarwidth: number;
    studycard?: boolean;
  }
>(({ theme, sidepanel, toolboxvisible, navigationbarwidth, studycard }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  overflow: 'hidden auto',
  maxWidth: '100%',
  height: '100%',
  paddingInline: studycard ? 0 : theme.spacing(6),
  '&.fullscreen': {
    paddingInline: theme.spacing(14),
  },
  '&.is-toolbox-visible': {
    maxWidth: `calc(100vw - ${theme.config.trajectoryToolboxWidth}px)`,
    marginRight: `${theme.config.trajectoryToolboxWidth}px`,
  },
  '&.is-toc-page': {
    marginRight: `56px`,
  },
  '&.is-animating-presentation-mode': {
    overflow: 'hidden',
  },
  '&.side-panel-open': {
    maxWidth: `calc(100dvw - ${sidepanel?.width as number}px - ${
      toolboxvisible === 'true' ? theme.config.trajectoryToolboxWidth : '0'
    }px - ${navigationbarwidth}px)`,
  },
  [theme.breakpoints.down('sm')]: {
    paddingInline: theme.spacing(3),
  },
  [theme.breakpoints.down('xs')]: {
    marginBottom: theme.spacing(7),
  },
  '.animate &': {
    transition: 'max-width ease-in-out 300ms',
  },
}));

const Wrapper = styled('div')<Theme, { maxwidth: string; studycard?: boolean }>(
  ({ theme, maxwidth, studycard }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    width: studycard ? 'unset' : '90%',
    maxWidth: studycard ? 'unset' : `${maxwidth}rem`,
    margin: studycard ? 0 : '4rem auto 0 auto',
    height: studycard ? '100%' : 'unset',
    paddingBottom: studycard ? 0 : theme.spacing(3),
    '@media print': {
      padding: 0,
    },
  })
);

const PrivatePageWrapper: FC<React.HTMLAttributes<HTMLDivElement>> = ({ children }) => {
  // hooks
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const location = useLocation();
  const currentRouteName = useAppSelector((state) => state.privatePageSettings.currentRouteName);

  // redux
  const toolboxVisible = useAppSelector((state) => state.trajectoryToolbox.toolboxVisible);
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const filterMode = useAppSelector((state) => state.filters.filterMode);
  const hasSubHeader = useAppSelector((state) => state.filters.hasSubHeader);
  const isSidePanelOpen = useAppSelector((state) => state.sidepanel.isOpen);
  const sidepanel = useAppSelector((state) => state.sidepanel.sidepanel);
  const currentSectionCode = useAppSelector((state) => state.trajectories.currentSectionCode);
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );
  const { isNavigationBarOpen } = useAppSelector((state) => state.navigationBar);
  const animation = useAppSelector((state) => state.presentationMode.animation);
  const place = useAppSelector((state) => state.navigationBar.place);

  // API Messages
  const blocksAPIErrorMessage = useAppSelector((state) => state.blocks.errorMessage);
  const modalProps = useAppSelector((state) => state.privatePageSettings.modalProps);
  const pageWidth = useAppSelector((state) => state.privatePageSettings.pageWidth);
  const currentBook = useAppSelector((state) => state.privatePageSettings.book);

  const { enqueueSnackbar } = useSnackbar();

  // mediaquery
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const isStudycardPage = location.pathname.indexOf('/3/studeren/') !== -1;
  const isTableOfContentsPage = place === 'table-of-contents';

  useEffect(() => {
    if (blocksAPIErrorMessage) {
      enqueueSnackbar(blocksAPIErrorMessage.text, {
        variant: blocksAPIErrorMessage.mode === 'api' ? 'error' : 'warning',
        anchorOrigin: {
          vertical: blocksAPIErrorMessage.mode === 'api' ? 'top' : 'bottom',
          horizontal: blocksAPIErrorMessage.mode === 'api' ? 'right' : 'center',
        },
        autoHideDuration: blocksAPIErrorMessage.mode === 'debt' ? 10000 : 5000,
      });
    }
  }, [blocksAPIErrorMessage, enqueueSnackbar]);

  useEffect(() => {
    dispatch(setActiveFilter(undefined));
    dispatch(setSelectedGroup(''));
    dispatch(setSelectedStudent(''));
  }, [currentBook?.id, dispatch]);

  const shouldShowNavbar = useMemo(() => {
    if (isMobile) return false;

    return (
      currentSectionCode === '0' ||
      currentSectionCode === '1' ||
      currentRouteName === 'chapterOverview' ||
      currentRouteName === 'bookOverview'
    );
  }, [currentSectionCode, isMobile, currentRouteName]);

  const getNavigationBarWidth = useCallback(() => {
    if (!shouldShowNavbar) return 0;
    if (isNavigationBarOpen) return theme.config.openNavigationBarWidth;
    return theme.config.closedNavigationBarWidth;
  }, [
    isNavigationBarOpen,
    shouldShowNavbar,
    theme.config.closedNavigationBarWidth,
    theme.config.openNavigationBarWidth,
  ]);

  return (
    <>
      <ImpersonateModal
        open={modalProps.opened}
        onClose={() => {
          dispatch(setModalProps({ ...modalProps, opened: false }));
        }}
      />
      <AppStepper />
      <ExternalWrapper
        id="private_page_external_wrapper"
        presentationmode={showPresentationMode}
        className={`${hasSubHeader ? 'has-subheader' : ''} ${
          filterMode === 'trajectory' &&
          activeFilter != null &&
          !activeFilterIsStudentFilter(activeFilter)
            ? 'filters-active'
            : ''
        }`}
      >
        {shouldShowNavbar && <NavigationBar />}
        <ScrollContainer
          id="private_page_scroll_container_unused"
          studycard={isStudycardPage}
          sidepanel={sidepanel}
          toolboxvisible={toolboxVisible ? 'true' : 'false'}
          navigationbarwidth={getNavigationBarWidth()}
          className={`${toolboxVisible && !isMobileScreen ? 'is-toolbox-visible' : ''} ${
            isSidePanelOpen ? 'side-panel-open animate' : ''
          } ${animation ? 'is-animating-presentation-mode' : ''} ${
            isTableOfContentsPage ? 'is-toc-page' : ''
          }`}
        >
          <Wrapper
            id="private_page_wrapper"
            maxwidth={pageWidth.toString()}
            studycard={isStudycardPage}
          >
            {children}
          </Wrapper>
        </ScrollContainer>
      </ExternalWrapper>
      <BlockAndSectionLoading />
    </>
  );
};

export default PrivatePageWrapper;
